/**
 * OLMPS Spectrum Engine — the weekly progression math.
 *
 * Pure, deterministic, side-effect-free functions. No DB, no React, no clock
 * reads inside the math (the caller passes weeks/timestamps in). Portable to
 * React Native. This is the one place a silent bug would corrode trust in the
 * whole product, so it is exhaustively unit-tested (spectrum.test.ts).
 *
 * The governing invariant: the avatar's position represents DEVELOPMENT, never
 * ENGAGEMENT. If a user could move it by merely opening the app, the number is
 * lying. Nothing here reads "did you open the tool" — only graded reps move it.
 *
 * See docs/OLMPS_SPECTRUM_PLAN.md for the design contract.
 */

// ---------------------------------------------------------------------------
// Constants (from the Spectrum Engine design doc)
// ---------------------------------------------------------------------------

/** Position bounds — the nine visible points on the line. */
export const POSITION_MIN = -4;
export const POSITION_MAX = 4;

/** Rolling-score EWMA weight (α): ~four-week memory. new = α·target + (1−α)·old. */
export const ALPHA = 0.35;

/**
 * Hysteresis: the displayed point only flips once the internal score crosses
 * this far PAST the midpoint boundary between two points, so the avatar never
 * flickers on a border.
 */
export const HYSTERESIS = 0.5;

/** Entropy drift applied to a tool with zero engagement in a week. */
export const ENTROPY_DRIFT = -0.15;

/** Securing thresholds. */
export const SECURE_POSITION = 3; // displayed ≥ +3 …
export const SECURE_CONSECUTIVE_REVEALS = 4; // … across 4 consecutive reveals
export const SECURE_MIN_REPS = 15; // 15–25 meaningful reps in that span
export const SECURE_MAX_REPS = 25;
export const SECURE_MIN_FRICTION_REPS = 3; // ≥3 reps graded +2 under real friction

/** The floor a secured tool gets — ordinary drift can never drag it negative. */
export const SECURED_FLOOR = 0;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** A single graded rep. `null`-grade opportunities are never recorded as reps. */
export interface Rep {
  /** −2 | −1 | +1 | +2. (0 / no-opportunity is never a rep.) */
  grade: -2 | -1 | 1 | 2;
  /** True when this was a +2 correct-use-under-detected-friction rep. */
  friction: boolean;
}

export type AcquisitionStatus = 'dormant' | 'training' | 'emerging' | 'stable' | 'secured';
export type MaintenanceStatus = 'active' | 'fading' | 'at_risk' | 'dormant';

/** The four separate layers — never collapsed into one number. */
export interface SpectrumState {
  /** Internal continuous score, −4.00..+4.00. */
  rolling: number;
  /** The displayed point, −4..+4 (with hysteresis vs. rolling). */
  displayed: number;
  /** Count of real (non-intake) reps banked — drives the confidence ring. */
  repCount: number;
  acquisition: AcquisitionStatus;
  /** Only meaningful once secured. */
  maintenance: MaintenanceStatus | null;
  /** 0 once secured (drift can't go below it); null otherwise. */
  floor: number | null;
  /** Set once, on secure. */
  securedAt: string | null;
}

/** One week's worth of evidence for one tool, as read from `dmt_events`. */
export interface WeeklyEvidence {
  reps: Rep[];
}

/** Rolling history needed to evaluate securing / maintenance at a reveal. */
export interface RevealHistory {
  /**
   * Displayed positions at the last N reveals (most-recent LAST), NOT yet
   * including the reveal being computed now. Used for the 4-consecutive check.
   */
  recentDisplayed: number[];
  /** Meaningful reps banked over the trailing securing window (last 4 reveals + this one). */
  windowReps: number;
  /** Friction (+2) reps banked over that same window. */
  windowFrictionReps: number;
  /**
   * True when the tool has passed its tool-specific certification challenge.
   * (Certification is Phase 7; until wired, pass `false` — nothing secures,
   * which is the correct conservative default.)
   */
  certified: boolean;
  /** For at-risk tracking: whether the tool was already <+1 at the previous reveal. */
  wasBelowOneLastReveal: boolean;
}

/** The computed outcome of a single tool's weekly reveal. */
export interface RevealResult {
  state: SpectrumState;
  oldDisplayed: number;
  newDisplayed: number;
  rollingBefore: number;
  rollingAfter: number;
  /** A significant event to animate/celebrate at the reveal, if any. */
  event: 'secured' | 'at_risk' | 'relapse' | null;
}

// ---------------------------------------------------------------------------
// Pure math
// ---------------------------------------------------------------------------

export function clampPosition(x: number): number {
  return Math.max(POSITION_MIN, Math.min(POSITION_MAX, x));
}

/**
 * Average of the week's graded reps. Returns `null` when there were no reps —
 * a zero-opportunity week is skipped entirely, never scored as 0 (that would
 * punish a week nothing came up). The caller must treat `null` as "no tally".
 */
export function weeklyRaw(reps: Rep[]): number | null {
  if (reps.length === 0) return null;
  const sum = reps.reduce((acc, r) => acc + r.grade, 0);
  return sum / reps.length;
}

/** EWMA step toward the week's raw target. Clamped to the position range. */
export function rollNext(old: number, raw: number): number {
  return clampPosition(ALPHA * raw + (1 - ALPHA) * old);
}

/**
 * Displayed point from the rolling score, with hysteresis: only move off the
 * previous displayed point once the rolling score has crossed HYSTERESIS past
 * the boundary toward a new integer point. Otherwise hold the previous point.
 */
export function displayedPosition(rolling: number, prevDisplayed: number): number {
  const target = clampPosition(Math.round(rolling));
  if (target === prevDisplayed) return prevDisplayed;
  // Distance the rolling score sits beyond the midpoint boundary between the
  // previous point and the candidate target.
  const boundary = (prevDisplayed + target) / 2;
  const crossed = Math.abs(rolling - boundary) >= HYSTERESIS && Math.sign(rolling - boundary) === Math.sign(target - prevDisplayed);
  return crossed ? target : prevDisplayed;
}

/** Acquisition ladder from the displayed position (pre-secure). */
export function acquisitionFromDisplayed(displayed: number): Exclude<AcquisitionStatus, 'secured'> {
  if (displayed <= 0) return 'dormant';
  if (displayed === 1) return 'training';
  if (displayed === 2) return 'emerging';
  return 'stable'; // +3 or +4 held, not yet secured
}

/** Maintenance state for a secured tool, from its current displayed position. */
export function maintenanceFromDisplayed(displayed: number, wasBelowOneLastReveal: boolean): MaintenanceStatus {
  if (displayed > 2) return 'active';
  if (displayed >= 1) return 'fading';
  // < +1
  return wasBelowOneLastReveal ? 'dormant' : 'at_risk';
}

/** Whether all four securing conditions are met at this reveal. */
export function meetsSecuring(newDisplayed: number, history: RevealHistory): boolean {
  const consecutive =
    newDisplayed >= SECURE_POSITION &&
    history.recentDisplayed.length >= SECURE_CONSECUTIVE_REVEALS - 1 &&
    history.recentDisplayed
      .slice(-(SECURE_CONSECUTIVE_REVEALS - 1))
      .every((p) => p >= SECURE_POSITION);
  return (
    consecutive &&
    history.windowReps >= SECURE_MIN_REPS &&
    history.windowFrictionReps >= SECURE_MIN_FRICTION_REPS &&
    history.certified
  );
}

/**
 * Compute one tool's weekly reveal: fold the week's evidence into the rolling
 * score, apply entropy drift when there was no engagement, recompute the
 * displayed point (hysteresis), respect a secured floor, and evaluate
 * securing / maintenance transitions.
 *
 * `now` is passed in (ISO string) so the math itself stays clock-free.
 */
export function computeReveal(
  prev: SpectrumState,
  evidence: WeeklyEvidence,
  history: RevealHistory,
  now: string,
): RevealResult {
  const rollingBefore = prev.rolling;
  const raw = weeklyRaw(evidence.reps);

  let rollingAfter: number;
  if (raw === null) {
    // No engagement this week → entropy drift toward the malady. Secured tools
    // are shielded from going below their floor.
    rollingAfter = clampPosition(rollingBefore + ENTROPY_DRIFT);
  } else {
    rollingAfter = rollNext(rollingBefore, raw);
  }

  // Secured floor: ordinary drift can never take a secured tool negative.
  if (prev.floor !== null) {
    rollingAfter = Math.max(prev.floor, rollingAfter);
  }

  const oldDisplayed = prev.displayed;
  const newDisplayed = displayedPosition(rollingAfter, oldDisplayed);

  const repCount = prev.repCount + evidence.reps.length;

  let event: RevealResult['event'] = null;
  let acquisition: AcquisitionStatus = prev.acquisition;
  let maintenance: MaintenanceStatus | null = prev.maintenance;
  let floor: number | null = prev.floor;
  let securedAt: string | null = prev.securedAt;

  if (prev.acquisition === 'secured') {
    // Already secured: track maintenance decay. Relapse (dormant) is the only
    // thing that can break the floor.
    maintenance = maintenanceFromDisplayed(newDisplayed, history.wasBelowOneLastReveal);
    if (maintenance === 'dormant') {
      event = 'relapse';
      floor = null; // floor broken on relapse
      acquisition = 'stable';
      maintenance = null;
    } else if (maintenance === 'at_risk') {
      event = 'at_risk';
    }
  } else if (meetsSecuring(newDisplayed, history)) {
    event = 'secured';
    acquisition = 'secured';
    maintenance = 'active';
    floor = SECURED_FLOOR;
    securedAt = now;
  } else {
    acquisition = acquisitionFromDisplayed(newDisplayed);
  }

  return {
    state: {
      rolling: rollingAfter,
      displayed: newDisplayed,
      repCount,
      acquisition,
      maintenance,
      floor,
      securedAt,
    },
    oldDisplayed,
    newDisplayed,
    rollingBefore,
    rollingAfter,
    event,
  };
}

/**
 * The zero-history initial state for a tool the user has never engaged and
 * never taken intake for. `intakePosition`, when provided (from the intake
 * diagnostic), seeds both the rolling score and the displayed point so nobody
 * starts at a meaningless 0.
 */
export function initialState(intakePosition?: number): SpectrumState {
  const seed = intakePosition ?? 0;
  const displayed = clampPosition(Math.round(seed));
  return {
    rolling: clampPosition(seed),
    displayed,
    repCount: 0,
    acquisition: acquisitionFromDisplayed(displayed),
    maintenance: null,
    floor: null,
    securedAt: null,
  };
}

// ---------------------------------------------------------------------------
// Top-line scores (aggregate across tools)
// ---------------------------------------------------------------------------

/** Olympian Score — how many tools are secured. Ratchets; only goes up (barring relapse). */
export function olympianScore(states: SpectrumState[]): number {
  return states.filter((s) => s.acquisition === 'secured').length;
}

/**
 * Mental Order — current aggregate position across tools, on a 0–100 scale.
 * Breathes up and down with the week. Maps each tool's displayed −4..+4 onto
 * 0..100 and averages; empty → 0.
 */
export function mentalOrder(states: SpectrumState[]): number {
  if (states.length === 0) return 0;
  const sum = states.reduce((acc, s) => acc + ((s.displayed - POSITION_MIN) / (POSITION_MAX - POSITION_MIN)) * 100, 0);
  return Math.round(sum / states.length);
}
